import React, { useEffect, useState } from 'react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';

const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

function buildUrl(path) {
  return `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;
}

async function apiFetch(path, options = {}) {
  const token = localStorage.getItem('accessToken');
  const response = await fetch(buildUrl(path), {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) throw new Error(data?.detail || `Request failed (${response.status})`);
  return data;
}

const EMPTY_FORM = {
  court_base_url: 'https://ecf.tneb.uscourts.gov',
  username: '',
  password: '',
  totp_secret: '',
  enabled: false,
};

export default function CMECFFilingSettings() {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const applySettings = (data) => {
    setSettings(data);
    setForm((current) => ({
      ...current,
      court_base_url: data?.court_base_url || EMPTY_FORM.court_base_url,
      username: '',
      password: '',
      totp_secret: '',
      enabled: Boolean(data?.enabled),
    }));
  };

  useEffect(() => {
    let active = true;
    apiFetch('/firm/cmecf-credentials/')
      .then((data) => {
        if (active) applySettings(data);
      })
      .catch((requestError) => {
        if (active) setError(requestError.message || 'Could not load CM/ECF settings.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, []);

  const save = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    const payload = {
      court_base_url: form.court_base_url,
      enabled: form.enabled,
    };
    if (form.username.trim()) payload.username = form.username.trim();
    if (form.password) payload.password = form.password;
    if (form.totp_secret.trim()) payload.totp_secret = form.totp_secret.trim();
    try {
      const data = await apiFetch('/firm/cmecf-credentials/', {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      applySettings(data);
      setMessage('CM/ECF settings saved.');
    } catch (requestError) {
      setError(requestError.message || 'Could not save CM/ECF settings.');
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    if (!window.confirm('Remove the saved PACER username, password, and authenticator secret?')) return;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await apiFetch('/firm/cmecf-credentials/', { method: 'DELETE' });
      applySettings({
        court_base_url: EMPTY_FORM.court_base_url,
        enabled: false,
        configured: false,
        ready_to_file: false,
        server_filing_enabled: settings?.server_filing_enabled,
        encryption_ready: settings?.encryption_ready,
        username_hint: '',
        has_password: false,
        has_totp_secret: false,
        last_verified_at: null,
        last_error: '',
      });
      setMessage('Saved CM/ECF credentials removed.');
    } catch (requestError) {
      setError(requestError.message || 'Could not remove CM/ECF credentials.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <section className="rounded-lg bg-white p-6 shadow">Loading CM/ECF settings…</section>;
  }

  return (
    <section className="rounded-lg bg-white p-6 shadow">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="max-w-3xl">
          <div className="flex items-center gap-2">
            <LockClosedIcon className="h-6 w-6 text-indigo-700" />
            <h2 className="text-xl font-semibold text-gray-900">CM/ECF Automatic Filing</h2>
          </div>
          <p className="mt-2 text-sm leading-6 text-gray-600">
            These settings are used only for a user-confirmed court filing. The PACER password and
            authenticator secret are encrypted; the six-digit authenticator code is generated only
            when a filing starts and is never saved.
          </p>
        </div>
        <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-semibold ${
          settings?.ready_to_file ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
        }`}>
          {settings?.ready_to_file ? <CheckCircleIcon className="h-5 w-5" /> : <ExclamationTriangleIcon className="h-5 w-5" />}
          {settings?.ready_to_file ? 'Ready to file' : 'Setup required'}
        </span>
      </div>

      {!settings?.encryption_ready && (
        <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          The server credential-encryption key is not configured. Credentials cannot be saved yet.
        </div>
      )}
      {!settings?.server_filing_enabled && (
        <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Live court filing is disabled on this deployment. It must be deliberately enabled after the
          workflow has been tested.
        </div>
      )}
      {settings?.last_error && (
        <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          Last sign-in/filing error: {settings.last_error}
        </div>
      )}
      {message && <div className="mt-4 rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">{message}</div>}
      {error && <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>}

      <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <label className="block">
          <span className="text-sm font-semibold text-gray-800">Court filing address</span>
          <input
            value={form.court_base_url}
            onChange={(event) => setForm((current) => ({ ...current, court_base_url: event.target.value }))}
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <span className="mt-1 block text-xs text-gray-500">Currently validated for TNEB CM/ECF.</span>
        </label>
        <label className="block">
          <span className="text-sm font-semibold text-gray-800">PACER username</span>
          <input
            autoComplete="username"
            value={form.username}
            onChange={(event) => setForm((current) => ({ ...current, username: event.target.value }))}
            placeholder={settings?.username_hint || 'Enter PACER username'}
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          {settings?.username_hint && <span className="mt-1 block text-xs text-gray-500">Leave blank to keep {settings.username_hint}.</span>}
        </label>
        <label className="block">
          <span className="text-sm font-semibold text-gray-800">PACER password</span>
          <input
            type="password"
            autoComplete="new-password"
            value={form.password}
            onChange={(event) => setForm((current) => ({ ...current, password: event.target.value }))}
            placeholder={settings?.has_password ? 'Saved — leave blank to keep' : 'Enter PACER password'}
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </label>
        <label className="block">
          <span className="text-sm font-semibold text-gray-800">Authenticator secret key</span>
          <input
            type="password"
            autoComplete="off"
            value={form.totp_secret}
            onChange={(event) => setForm((current) => ({ ...current, totp_secret: event.target.value }))}
            placeholder={settings?.has_totp_secret ? 'Saved — leave blank to keep' : 'Base32 secret from PACER MFA setup'}
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <span className="mt-1 block text-xs text-gray-500">
            Enter the setup secret, not a temporary six-digit code. This uses PACER&apos;s supported TOTP process; it does not bypass MFA.
          </span>
        </label>
      </div>

      <label className="mt-5 flex items-start gap-3 rounded-md border border-gray-200 bg-gray-50 p-4">
        <input
          type="checkbox"
          checked={form.enabled}
          onChange={(event) => setForm((current) => ({ ...current, enabled: event.target.checked }))}
          className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600"
        />
        <span>
          <span className="block text-sm font-semibold text-gray-900">Enable automatic Financial Management filings</span>
          <span className="mt-1 block text-xs leading-5 text-gray-600">
            Every filing still requires a separate certificate-type and redaction confirmation on the todo.
          </span>
        </span>
      </label>

      <div className="mt-5 flex flex-wrap gap-3">
        <button
          type="button"
          onClick={save}
          disabled={saving || !settings?.encryption_ready}
          className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
        >
          {saving ? 'Saving…' : 'Save CM/ECF Settings'}
        </button>
        {settings?.configured && (
          <button
            type="button"
            onClick={clear}
            disabled={saving}
            className="rounded-md border border-red-300 bg-white px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-60"
          >
            Remove Saved Credentials
          </button>
        )}
      </div>
      {settings?.last_verified_at && (
        <p className="mt-3 text-xs text-gray-500">
          Last successful PACER authentication: {new Date(settings.last_verified_at).toLocaleString()}
        </p>
      )}
    </section>
  );
}

